// Hero Carousel — case-study slide paginator.
//
// Manual pagination: Tiny Slider runs the slide engine (gallery/fade transition,
// touch swipe, keyboard arrows, accessibility live-region). The prev / dot row /
// next buttons in the template are wired to call `slider.goTo()` directly so the
// combined-row pagination control in the Figma spec is straightforward to style.
//
// Init contract:
//   - One block per page is expected; the module finds every `.hero-carousel`
//     element so the home page rendering does not depend on a specific id.
//   - If a block contains only one slide (template adds `.hero-carousel--single`)
//     Tiny Slider is not initialised — the static markup is sufficient.

import { tns } from "/node_modules/tiny-slider/src/tiny-slider.js";

const heroCarousel = {
    init: function () {
        const carousels = document.querySelectorAll(".hero-carousel");
        if (!carousels.length) {
            return;
        }

        const prefersReducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        carousels.forEach((carousel) => initCarousel(carousel, prefersReducedMotion));
    },
};

function initCarousel(carousel, prefersReducedMotion) {
    const track = carousel.querySelector(".hero-carousel__track");
    if (!track) {
        return;
    }

    const slideCount = parseInt(carousel.dataset.slideCount || "0", 10);
    const pauseBtn = carousel.querySelector(".hero-carousel__pause");

    // Single-slide carousel: skip Tiny Slider entirely. The static markup
    // already renders the one slide and the template suppresses the controls.
    if (slideCount <= 1 || carousel.classList.contains("hero-carousel--single")) {
        return;
    }

    // Autoplay is gated on the OS reduce-motion preference. SC 2.2.2 only
    // applies to content that actually moves, so when reduced motion is on
    // we skip autoplay entirely AND suppress the pause control (it would
    // sit in the tab order with no associated motion to pause).
    const shouldAutoplay = !prefersReducedMotion;

    const slider = tns({
        container: track,
        items: 1,
        mode: "gallery",
        animateIn: "tns-fadeIn",
        animateOut: "tns-fadeOut",
        speed: prefersReducedMotion ? 0 : 600,
        autoplay: shouldAutoplay,
        autoplayTimeout: 6000,
        // Tiny Slider's built-in hover-pause / visibility-pause unconditionally
        // resume autoplay when the trigger condition ends — which overrides an
        // explicit user pause. Both are handled below, gated on `userPaused`.
        autoplayHoverPause: false,
        autoplayResetOnVisibility: false,
        autoplayButton: false,
        autoplayButtonOutput: false,
        controls: false,
        nav: false,
        loop: true,
        arrowKeys: true,
        touch: true,
        mouseDrag: true,
        swipeAngle: 30,
        preventScrollOnTouch: "auto",
        slideBy: 1,
    });

    if (!slider || typeof slider.events?.on !== "function") {
        if (pauseBtn) {
            pauseBtn.hidden = true;
        }
        return;
    }

    if (!shouldAutoplay && pauseBtn) {
        pauseBtn.hidden = true;
    }

    const dots = carousel.querySelectorAll(".hero-carousel__dot");
    const prev = carousel.querySelector(".hero-carousel__arrow--prev");
    const next = carousel.querySelector(".hero-carousel__arrow--next");

    prev?.addEventListener("click", () => slider.goTo("prev"));
    next?.addEventListener("click", () => slider.goTo("next"));

    dots.forEach((dot) => {
        dot.addEventListener("click", () => {
            const index = parseInt(dot.dataset.slideIndex || "0", 10);
            slider.goTo(index);
        });
    });

    slider.events.on("indexChanged", (info) => {
        dots.forEach((dot, i) => {
            const isActive = i === info.displayIndex - 1;
            dot.classList.toggle("is-active", isActive);
            dot.setAttribute("aria-current", isActive ? "true" : "false");
        });
        syncSlideInert(carousel);
    });

    // Tiny Slider applies `aria-hidden="true"` to every inactive slide and to
    // its cloned slides (used for infinite loop). Those slides still contain
    // a focusable CTA link, which trips axe-core's `aria-hidden-focus` rule
    // (focusable descendants of aria-hidden elements). Mirroring `inert` onto
    // the same elements removes the subtree from the tab order AND the
    // accessibility tree, resolving the contradiction.
    syncSlideInert(carousel);

    // WCAG 2.2.2 pause control + sticky user-pause state.
    //
    // `userPaused` is the source of truth for whether the user has *explicitly*
    // asked autoplay to stop. Hover, focus, and visibility events all gate
    // their auto-resume on `!userPaused` so a deliberate pause is never
    // silently overridden by ambient interactions.
    if (shouldAutoplay && pauseBtn) {
        let userPaused = false;

        const labelPause = pauseBtn.dataset.labelPause || "Pause carousel";
        const labelPlay = pauseBtn.dataset.labelPlay || "Play carousel";

        pauseBtn.addEventListener("click", () => {
            if (userPaused) {
                slider.play();
                userPaused = false;
                pauseBtn.setAttribute("aria-pressed", "false");
                pauseBtn.textContent = labelPause;
            } else {
                slider.pause();
                userPaused = true;
                pauseBtn.setAttribute("aria-pressed", "true");
                pauseBtn.textContent = labelPlay;
            }
        });

        const controls = carousel.querySelector(".hero-carousel__controls");
        if (controls) {
            controls.addEventListener("mouseenter", () => {
                if (!userPaused) slider.pause();
            });
            controls.addEventListener("mouseleave", () => {
                if (!userPaused) slider.play();
            });
            controls.addEventListener("focusin", () => {
                if (!userPaused) slider.pause();
            });
            controls.addEventListener("focusout", (e) => {
                // Ignore focus moves within the controls row — only resume
                // when focus leaves the controls entirely.
                if (controls.contains(e.relatedTarget)) return;
                if (!userPaused) slider.play();
            });
        }

        document.addEventListener("visibilitychange", () => {
            if (document.hidden) {
                slider.pause();
            } else if (!userPaused) {
                slider.play();
            }
        });
    }

    // Mirror the slider.js:30 a11y fix: Tiny Slider's outer wrapper gets a
    // `role="generic"` plus `aria-label`, which is an invalid combination.
    // The controls container disappears (controls: false above) but the
    // outer node still gets the same problematic markup.
    const outer = track.closest(".tns-outer");
    if (outer) {
        const controlsEl = outer.querySelector(".tns-controls");
        if (controlsEl) {
            controlsEl.setAttribute("role", "group");
        }
    }
}

function syncSlideInert(carousel) {
    const slides = carousel.querySelectorAll(".hero-carousel__slide");
    slides.forEach((slide) => {
        slide.inert = slide.getAttribute("aria-hidden") === "true";
    });
}

export default heroCarousel;
